/**
 * Inline-editable table cell. Click to edit, blur/Enter to save, Tab to navigate.
 */

import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { ValueCell } from "./tableStyles";

interface EditableCellProps {
  value: number;
  onChange: (value: number) => void;
  decimals?: number;
  min?: number;
  max?: number;
  /** Arrow-key / spinner increment. Defaults to 10^(-decimals). */
  step?: number;
  suffix?: string;
  disabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Extra sx applied to the cell wrapper */
  cellSx?: Record<string, unknown>;
}

export default function EditableCell({
  value,
  onChange,
  decimals = 1,
  min = 0,
  max,
  step,
  suffix,
  disabled,
  onMouseEnter,
  onMouseLeave,
  cellSx,
}: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.select();
    }
  }, [editing]);

  const handleStartEdit = useCallback(() => {
    if (disabled) return;
    setEditValue(value.toFixed(decimals));
    setEditing(true);
  }, [value, decimals, disabled]);

  const handleCommit = useCallback(() => {
    setEditing(false);
    const parsed = parseFloat(editValue);
    if (isNaN(parsed)) return;
    const clamped = Math.max(
      min,
      max !== undefined ? Math.min(parsed, max) : parsed
    );
    const factor = Math.pow(10, decimals);
    const rounded = Math.round(clamped * factor) / factor;
    // Compare at the same precision to avoid false positives when value has
    // more decimal places than `decimals` allows (e.g. 10.24 displayed as "10.2").
    const existingRounded = Math.round(value * factor) / factor;
    if (rounded !== existingRounded) {
      onChange(rounded);
    }
  }, [editValue, min, max, decimals, onChange, value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleCommit();
      } else if (e.key === "Escape") {
        setEditing(false);
      }
    },
    [handleCommit]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setEditValue(raw);
      // Live-update: propagate valid numbers immediately
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) {
        const clamped = Math.max(
          min,
          max !== undefined ? Math.min(parsed, max) : parsed
        );
        const factor = Math.pow(10, decimals);
        const rounded = Math.round(clamped * factor) / factor;
        const existingRounded = Math.round(value * factor) / factor;
        if (rounded !== existingRounded) {
          onChange(rounded);
        }
      }
    },
    [min, max, decimals, onChange, value]
  );

  if (editing) {
    return (
      <ValueCell
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{ p: 0.25, ...cellSx }}
      >
        <StyledInput
          inputRef={inputRef}
          value={editValue}
          onChange={handleChange}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          size="small"
          type="number"
          slotProps={{
            htmlInput: { min, max, step: step ?? Math.pow(10, -decimals) },
          }}
          autoFocus
        />
      </ValueCell>
    );
  }

  return (
    <ValueCell
      onClick={handleStartEdit}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        cursor: disabled ? "default" : "pointer",
        transition: "background-color 0.15s",
        p: disabled ? undefined : "4px 6px",
        "& > .editable-inner": {
          backgroundColor: "#f9fafb",
          border: "1px solid #d1d5db",
          borderRadius: "4px",
          padding: "2px 8px",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)",
          display: "block",
          textAlign: "right",
        },
        "&:hover > .editable-inner": disabled
          ? {}
          : {
              borderColor: "#9ca3af",
              backgroundColor: "#f3f4f6",
            },
        ...cellSx,
      }}
    >
      {disabled ? (
        <>
          {value.toFixed(decimals)}
          {suffix}
        </>
      ) : (
        <span className="editable-inner">
          {value.toFixed(decimals)}
          {suffix}
        </span>
      )}
    </ValueCell>
  );
}

const StyledInput = styled(TextField)({
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    borderRadius: 4,
  },
  "& .MuiInputBase-input": {
    padding: "4px 8px",
    textAlign: "right",
    fontSize: "0.8125rem",
    width: 52,
  },
});
